/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import {
    StyledModal,
    Button,
    Cell,
    StickyBottomHelper,
    Icon,
    FlexRow,
    FlexCol,
    Row,
    HtmlParser,
    Heading,
    SecurityHelper,
    Alert,
} from 'sarsaparilla';
import { FavoriteHeart } from 'shared-ui/favorites';
import { getGatewayUrl } from 'shared-ui/utils/getGatewayUrl';
import { LoadingWrapper } from 'ui-lottery';
import { FeatureFlagArray } from './featureFlag/FeatureFlagArray';
import { FacilityProps, PassTypeProps, ReservationProps } from '../props';
import { formatPrice } from '../utils';
import { passSelectionRoute } from '../../dev/routesArray';
import NeedToKnow from './NeedToKnow';
import Overview from './Overview';
import PageRegion from './layout/PageRegion';
import PassBottomNav from './PassBottomNav';
import PassInfoForm from './PassInfoForm';
import PassRatesTable from './PassRatesTable';
import PassSelectionError from '../containers/PassSelectionError';
import PassTypesTable from './PassTypesTable';
import RenderIf from '../utils/JSXUtils';
import ReservationSummary from './ReservationSummary';

export default class PassSelection extends PureComponent {
    static defaultProps = {
        loading: {},
        passCounts: {},
        passTypes: [],
    };

    static propTypes = {
        children: PropTypes.node.isRequired,
        facilityId: PropTypes.string,
        handleNeedToKnowChecked: PropTypes.func.isRequired,
        loaded: PropTypes.shape({
            reservations: PropTypes.bool,
            passTypes: PropTypes.bool,
        }).isRequired,
        loading: PropTypes.shape({
            facilities: PropTypes.bool,
            passTypes: PropTypes.bool,
        }).isRequired,
        maxTotalGuestsReached: PropTypes.bool.isRequired,
        needToKnowChecked: PropTypes.bool.isRequired,
        onPassCountChange: PropTypes.func.isRequired,
        onRemoveReservation: PropTypes.func.isRequired,
        onReservationChange: PropTypes.func.isRequired,
        passCounts: PropTypes.object,
        passTypeId: PropTypes.string,
        passTypes: PropTypes.arrayOf(PassTypeProps),
        proceedButton: PropTypes.node.isRequired,
        reservationId: PropTypes.string,
        reservations: PropTypes.arrayOf(ReservationProps),
        submitAttempted: PropTypes.bool.isRequired,
        lastSubmitAttempt: PropTypes.number.isRequired,
        facility: FacilityProps,
        parentAsset: PropTypes.array,
        verifyAddress: PropTypes.func,
    };

    state = {
        isSummaryModalOpen: false,
    };

    handlePassInfoChange = (reservationId, passTypeId, passInfo, isValid) => {
        const reservation = this.props.reservations.find(
            ({ id }) => id === reservationId
        );
        if (reservation) {
            // Default "discount" feature.
            const discount = {};

            // Format "accepted" feature.
            const passType = this.props.passTypes.find(
                ({ id }) => id === reservation.pass_type_id
            );
            const accepted = {};
            if (passType && passType.features.accepted) {
                accepted.accepted = passType.features.accepted.reduce(
                    (memo, key) => ({
                        ...memo,
                        [key]: true,
                    }),
                    {}
                );
            }

            // Build updated reservation.
            const updatedReservation = {
                ...reservation,
                pass_info: passInfo,

                // New API updates:
                pass_type_id: passTypeId || reservation.pass_type_id,
                holder_info: passInfo.holder_info,
                features: {
                    discount,
                    ...passInfo.features,
                    ...accepted,
                },
            };

            this.props.onReservationChange(updatedReservation, isValid);
        }
    };

    toggleSummaryModal = () =>
        this.setState((prevState) => ({
            isSummaryModalOpen: !prevState.isSummaryModalOpen,
        }));

    render() {
        const {
            children,
            facility,
            facilityId,
            loaded,
            loading,
            maxTotalGuestsReached,
            needToKnowChecked,
            passCounts,
            parentAsset,
            passTypes,
            proceedButton,
            reservationId,
            reservations,
            submitAttempted,
            lastSubmitAttempt,
        } = this.props;

        if (!facilityId) {
            return null;
        }

        let pricing = null;
        if (facility.id && Array.isArray(get(facility, 'contents.pricing'))) {
            const pricingContent = facility.contents.pricing.flatMap(
                ({ paragraphs = [] }, p) =>
                    paragraphs.map((html, n) => (
                        <p key={`html-${p}-${n}`}>
                            <HtmlParser html={html} />
                        </p>
                    ))
            );

            pricing = (
                <Alert type="info" hasAriaLive>
                    {pricingContent}
                </Alert>
            );
        }

        const { isSummaryModalOpen } = this.state;
        const reservationSummary = (
            <ReservationSummary
                passTypes={passTypes}
                reservations={reservations}
                facility={facility}
            >
                {children}
            </ReservationSummary>
        );
        const recarea = parentAsset?.find(
            (item) => item.entity_id === facility.parent_asset_id
        );
        const isWidget = SecurityHelper.isWidgetSession();

        const subheader = (
            <ul className={'rec-subhead-wrap'}>
                {!isNil(recarea) && (
                    <li>
                        Part of{' '}
                        <a
                            href={getGatewayUrl(facility?.parent_asset_id)}
                            rel="noreferrer"
                            target="_blank"
                            translate="no"
                            aria-label={`${recarea?.name} (opens in a new tab)`}
                        >
                            {recarea?.name}
                        </a>
                    </li>
                )}
                <li>
                    <FavoriteHeart
                        entityId={facilityId}
                        entityType={'activitypass'}
                        showFavoriteText
                        key="favorite-button"
                    />
                </li>
            </ul>
        );

        return (
            <div className="activitypass-order-details">
                <div className="rec-nested-wrap">
                    <div id="page-body" className="rec-activitypass-left-rail">
                        <div className="mobile-only">
                            <Heading headingLevel={1} appearance="h2" translate="no">
                                {facility.name}
                            </Heading>
                            {!isWidget && subheader}
                        </div>
                        <div className="non-mobile-only">
                            <Heading headingLevel={1}>Option Selection</Heading>
                            <Heading headingLevel={2} translate="no">
                                {facility.name}
                            </Heading>
                            {!isWidget && subheader}
                        </div>

                        <PassSelectionError
                            reservations={reservations}
                            submitAttempted={submitAttempted}
                            lastSubmitAttempt={lastSubmitAttempt}
                            needToKnowChecked={needToKnowChecked}
                        />

                        {!isWidget && facility?.contents?.overview && (
                            <Overview overview={get(facility, 'contents.overview', [])} />
                        )}

                        {RenderIf(
                            loading.passTypes || (loaded.passTypes && !reservationId)
                        )(
                            <LoadingWrapper loading={loading.passTypes} size="lg">
                                <FeatureFlagArray
                                    flag="disableNpsParkpasses"
                                    flagValueToMatch={facilityId}
                                    fallback={
                                        <PageRegion>
                                            <Row>
                                                <Cell>
                                                    <PassRatesTable
                                                        passTypes={passTypes}
                                                    />
                                                </Cell>
                                            </Row>
                                            <Row>
                                                <Cell>
                                                    {reservationId && (
                                                        <Heading
                                                            headingLevel={3}
                                                            appearance="h4"
                                                            className="activitypass-section-heading"
                                                        >
                                                            Modify
                                                        </Heading>
                                                    )}
                                                    <p>
                                                        Select the option you would like
                                                        to purchase from the available
                                                        choices below:
                                                    </p>
                                                    <PassTypesTable
                                                        maxTotalGuestsReached={
                                                            maxTotalGuestsReached
                                                        }
                                                        passCounts={passCounts}
                                                        passTypeId={this.props.passTypeId}
                                                        passTypes={passTypes}
                                                        pricing={pricing}
                                                        onChange={
                                                            this.props.onPassCountChange
                                                        }
                                                    />
                                                </Cell>
                                            </Row>
                                        </PageRegion>
                                    }
                                >
                                    <PageRegion>
                                        <div aria-live="polite">
                                            <Alert
                                                type="warning"
                                                heading="Coronavirus (COVID-19) Update"
                                                hasAriaLive
                                                className="activitypass-covid19-notification"
                                                id="activitypass-corona-virus-notification"
                                            >
                                                Please be aware that sales are currently
                                                suspended for the activity pass at this
                                                location due to impacts from the
                                                coronavirus (COVID-19). We will provide
                                                updates as the situation evolves. We
                                                appreciate your patience and
                                                understanding.
                                            </Alert>
                                        </div>
                                    </PageRegion>
                                </FeatureFlagArray>
                            </LoadingWrapper>
                        )}

                        {RenderIf(reservations.length)(() => (
                            <div>
                                <Heading
                                    headingLevel={4}
                                    className="activitypass-section-heading h4"
                                >
                                    {reservationId ? 'Modify' : 'Order Details'}
                                </Heading>
                                <p>
                                    {reservationId
                                        ? 'Please modify the information below:'
                                        : 'Please provide the required information below:'}
                                </p>
                                {reservations.map((reservation, number) => {
                                    const passType = passTypes.find(
                                        ({ id }) => id === reservation.pass_type_id
                                    );
                                    if (!passType) {
                                        return null;
                                    }
                                    return (
                                        <Row key={`${reservation.id}`}>
                                            <Cell>
                                                <PassInfoForm
                                                    submitAttempted={submitAttempted}
                                                    facility={facility}
                                                    number={number + 1}
                                                    passInfo={reservation.pass_info}
                                                    passType={passType}
                                                    price={formatPrice(reservation.price)}
                                                    renderRemove
                                                    isPending={reservation.is_pending}
                                                    reservationId={reservation.id}
                                                    onChange={this.handlePassInfoChange.bind(
                                                        this,
                                                        reservation.id
                                                    )}
                                                    onRemove={
                                                        this.props.onRemoveReservation
                                                    }
                                                    updateAddToCartState={
                                                        this.props.updateAddToCartState
                                                    }
                                                    verifyAddress={
                                                        this.props.verifyAddress
                                                    }
                                                />
                                            </Cell>
                                        </Row>
                                    );
                                })}
                            </div>
                        ))}

                        {RenderIf(reservationId)(() => (
                            <Button
                                appearance="secondary"
                                size="sm"
                                className="activitypass-add-another-pass"
                                onClick={() => {
                                    window.location = passSelectionRoute(facility.id);
                                    return null;
                                }}
                                iconBeforeElement={<Icon iconName="add-circle" />}
                            >
                                ADD ANOTHER OPTION
                            </Button>
                        ))}

                        {RenderIf(facility.id)(() => (
                            <div className="activitypass-info-box">
                                <NeedToKnow
                                    needToKnow={get(
                                        facility,
                                        'contents.need_to_know',
                                        []
                                    )}
                                    className={
                                        submitAttempted && !needToKnowChecked
                                            ? 'rec-error'
                                            : ''
                                    }
                                    siteName={facility.name}
                                    noFeePeriods={facility.free_days}
                                    checked={needToKnowChecked}
                                    isInvalid={submitAttempted && !needToKnowChecked}
                                    onChange={this.props.handleNeedToKnowChecked}
                                />
                            </div>
                        ))}

                        {facility.id && !!reservations.length && (
                            <StickyBottomHelper scrollingContainerId="page-body">
                                <div className="rec-order-detail-page-actions">
                                    <FlexRow hasGutters={false}>
                                        <FlexCol xs={6} md={8} alignSelf="center">
                                            <button
                                                onClick={() => this.toggleSummaryModal()}
                                                className="rec-button-link display-sm-block"
                                                type="button"
                                            >
                                                {'Show Summary'}
                                            </button>
                                        </FlexCol>
                                        <FlexCol xs={6} md={4}>
                                            <PassBottomNav
                                                proceedButton={proceedButton}
                                            />
                                        </FlexCol>
                                    </FlexRow>
                                </div>
                            </StickyBottomHelper>
                        )}

                        <StyledModal
                            isOpen={isSummaryModalOpen}
                            onRequestClose={this.toggleSummaryModal}
                            role="complementary"
                            heading="Summary"
                            size="md"
                            contentLabel="Order Summary"
                            className="rec-modal-order-summary"
                        >
                            <div className="rec-modal-content-wrap">
                                {reservationSummary}
                            </div>
                        </StyledModal>
                    </div>

                    <div className="rec-activitypass-right-rail">
                        {facility.id && reservationSummary}
                    </div>
                </div>
            </div>
        );
    }
}

// cspell:ignore recarea, noreferrer, activitypass, parkpasses
